/**
 * PDF Compressor — apps.leferi.com/pdfcompressor
 *
 * Pattern follows YoutubeDownloader: Landing → Progress → Complete
 * three-state view, polling for progress between submit and done.
 *
 * Much simpler than the YT page:
 *   - No terms modal (no third-party ToS to mirror)
 *   - No metadata probe / thumbnail
 *   - No multi-quality matrix — just three Ghostscript presets
 *   - No beta gate — open to all @leferi.com users from launch
 *
 * Layout follows LCMD: square corners, brand-pink primary CTA, brown
 * ink, ▪ marker, no emoji.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ChevronLeft,
  Download,
  FileText,
  Upload,
  X as XIcon,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import type { AuthSession } from './auth'
import {
  cancelPdfJob,
  getPdfHistory,
  getPdfJob,
  getPdfStatus,
  pdfDownloadUrl,
  uploadPdf,
  type PdfJob,
  type PdfPreset,
} from './pdfApi'

const POLL_MS = 1200
const LS_JOB_KEY = 'leferi.pdf.activeJob'

type Preset = PdfPreset

const PRESET_OPTIONS: {
  value: Preset
  label: string
  description: string
  recommended?: boolean
}[] = [
  {
    value: 'screen',
    label: 'Screen',
    description: '72dpi · 가장 작은 용량. 텍스트만 있는 PDF에 권장.',
  },
  {
    value: 'ebook',
    label: 'eBook',
    description: '150dpi · AI 업로드 권장. 화질-용량 균형 최적.',
    recommended: true,
  },
  {
    value: 'printer',
    label: 'Printer',
    description: '300dpi · 인쇄 품질 유지. 압축률은 낮음.',
  },
]

type View =
  | { kind: 'landing' }
  | { kind: 'progress'; jobId: string }
  | { kind: 'complete'; job: PdfJob }

export function PdfCompressorPage({
  session: _session,
}: {
  session: AuthSession
}) {
  const [view, setView] = useState<View>({ kind: 'landing' })
  const [history, setHistory] = useState<PdfJob[] | null>(null)
  const [uploadLimitBytes, setUploadLimitBytes] = useState(500 * 1024 * 1024)
  const [betaBlocked, setBetaBlocked] = useState(false)

  useEffect(() => {
    const stored = window.localStorage.getItem(LS_JOB_KEY)
    if (stored) setView({ kind: 'progress', jobId: stored })
    void Promise.all([getPdfStatus(), getPdfHistory()])
      .then(([s, h]) => {
        setUploadLimitBytes(s.uploadLimitBytes)
        setHistory(h)
      })
      .catch((err: unknown) => {
        // The router returns 403 beta_only when the signed-in user
        // isn't on PDF_ALLOWED_EMAILS. Show a friendly overlay rather
        // than the upload form they can't actually use.
        if (err instanceof Error && err.message === 'beta_only') {
          setBetaBlocked(true)
        }
        setHistory([])
      })
  }, [])

  if (betaBlocked) {
    return (
      <div className="pt-s-7">
        <Link
          to="/"
          className="inline-flex items-center gap-s-2 rounded-r-1 border border-line-hairline bg-surface-base px-s-3 py-s-2 lf-body-sm text-fg-2 transition-colors duration-base ease-standard hover:border-line-soft hover:bg-surface-soft hover:text-fg-1 focus-visible:outline-none focus-visible:shadow-focus"
        >
          <ChevronLeft size={14} strokeWidth={1.5} />
          Apps
        </Link>
        <div className="mt-s-9 border border-line-hairline p-s-9 text-center">
          <p className="lf-eyebrow">▪ Closed beta · 비공개 베타</p>
          <h1 className="mt-s-3 lf-display-2">Coming soon.</h1>
          <p className="mt-s-4 mx-auto max-w-[480px] lf-body text-fg-2">
            PDF Compressor는 현재 비공개 베타 단계예요. 안정화 후 전체
            공개 예정이에요.
          </p>
          <p className="mt-s-2 mx-auto max-w-[480px] lf-caption text-fg-3">
            This tool is in closed beta and isn&apos;t available to you yet.
          </p>
        </div>
      </div>
    )
  }

  const reloadHistory = useCallback(() => {
    void getPdfHistory().then(setHistory).catch(() => undefined)
  }, [])

  const startJob = useCallback((jobId: string) => {
    window.localStorage.setItem(LS_JOB_KEY, jobId)
    setView({ kind: 'progress', jobId })
  }, [])

  const finishJob = useCallback(
    (job: PdfJob) => {
      window.localStorage.removeItem(LS_JOB_KEY)
      setView({ kind: 'complete', job })
      reloadHistory()
    },
    [reloadHistory],
  )

  const backToLanding = useCallback(() => {
    window.localStorage.removeItem(LS_JOB_KEY)
    setView({ kind: 'landing' })
    reloadHistory()
  }, [reloadHistory])

  return (
    <div className="pt-s-7">
      <Link
        to="/"
        className="inline-flex items-center gap-s-2 rounded-r-1 border border-line-hairline bg-surface-base px-s-3 py-s-2 lf-body-sm text-fg-2 transition-colors duration-base ease-standard hover:border-line-soft hover:bg-surface-soft hover:text-fg-1 focus-visible:outline-none focus-visible:shadow-focus"
      >
        <ChevronLeft size={14} strokeWidth={1.5} />
        Apps
      </Link>

      <div className="mt-s-6">
        <p className="lf-eyebrow">▪ PDF Compressor · PDF 압축</p>
        <h1 className="mt-s-2 lf-display-2">Shrink a PDF.</h1>
        <p className="mt-s-3 max-w-[640px] lf-body text-fg-2">
          Claude 등 AI 업로드 한도에 맞춰 PDF 용량을 줄여요.
        </p>
        <p className="mt-s-3 max-w-[640px] lf-body text-fg-2">
          텍스트 레이아웃은 그대로 두고 내부 이미지 해상도만
          다운샘플링해요.
        </p>
      </div>

      {view.kind === 'landing' ? (
        <UploadForm
          uploadLimitBytes={uploadLimitBytes}
          onStarted={startJob}
        />
      ) : null}

      {view.kind === 'progress' ? (
        <ProgressView
          jobId={view.jobId}
          onComplete={finishJob}
          onCancel={backToLanding}
        />
      ) : null}

      {view.kind === 'complete' ? (
        <CompleteView job={view.job} onAgain={backToLanding} />
      ) : null}

      <HistoryList items={history} />
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Upload form                                                                 */
/* -------------------------------------------------------------------------- */

function UploadForm({
  uploadLimitBytes,
  onStarted,
}: {
  uploadLimitBytes: number
  onStarted: (jobId: string) => void
}) {
  const [file, setFile] = useState<File | null>(null)
  const [preset, setPreset] = useState<Preset>('ebook')
  const [dragOver, setDragOver] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement | null>(null)

  const limitMb = useMemo(() => Math.round(uploadLimitBytes / 1024 / 1024), [
    uploadLimitBytes,
  ])

  const onChoose = (f: File | null) => {
    setError(null)
    if (!f) return
    if (!/\.pdf$/i.test(f.name) && f.type !== 'application/pdf') {
      setError('PDF 파일만 업로드할 수 있어요.')
      return
    }
    if (f.size > uploadLimitBytes) {
      setError(`${limitMb}MB 이하 파일만 업로드할 수 있어요.`)
      return
    }
    setFile(f)
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!file) return
    setBusy(true)
    setError(null)
    try {
      const r = await uploadPdf(file, preset)
      onStarted(r.jobId)
    } catch (err) {
      const msg =
        err instanceof Error ? humanizeError(err.message) : '업로드 실패'
      setError(msg)
      setBusy(false)
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      className="mt-s-7 grid grid-cols-1 gap-s-6 border border-line-hairline p-s-6 lg:grid-cols-[1fr_minmax(220px,260px)]"
    >
      <div className="flex flex-col gap-s-6">
        <Field label="File · 파일">
          <div
            onDragEnter={(e) => {
              e.preventDefault()
              setDragOver(true)
            }}
            onDragOver={(e) => {
              e.preventDefault()
              setDragOver(true)
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault()
              setDragOver(false)
              const f = e.dataTransfer.files?.[0] ?? null
              onChoose(f)
            }}
            className={`flex flex-col items-center justify-center gap-s-3 border-dashed border-2 px-s-4 py-s-9 transition-colors duration-base ease-standard ${
              dragOver
                ? 'border-brand-pink bg-pink-50'
                : 'border-line-hairline bg-surface-base'
            }`}
          >
            {file ? (
              <>
                <FileText size={32} strokeWidth={1.4} className="text-fg-2" />
                <div className="text-center">
                  <p className="lf-body text-fg-1">{file.name}</p>
                  <p className="mt-s-1 lf-body-sm lf-num text-fg-3">
                    {formatBytes(file.size)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setFile(null)
                    if (fileRef.current) fileRef.current.value = ''
                  }}
                  className="inline-flex items-center gap-s-1 rounded-r-1 border border-line-hairline bg-surface-base px-s-3 py-s-2 lf-body-sm text-fg-2 transition-colors duration-base ease-standard hover:border-line-soft hover:text-fg-1"
                >
                  <XIcon size={13} strokeWidth={1.5} />
                  Remove
                </button>
              </>
            ) : (
              <>
                <Upload size={28} strokeWidth={1.4} className="text-fg-3" />
                <p className="lf-body text-fg-2">
                  PDF를 여기로 드래그하거나
                </p>
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="rounded-r-1 border border-line-hairline bg-surface-base px-s-3 py-s-2 lf-body-sm text-fg-1 transition-colors duration-base ease-standard hover:border-line-soft"
                >
                  파일 선택 · Choose file
                </button>
              </>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="application/pdf,.pdf"
              className="hidden"
              onChange={(e) => onChoose(e.target.files?.[0] ?? null)}
            />
          </div>
        </Field>

        <Field
          label="Preset · 압축 강도"
          hint={`최대 ${limitMb}MB까지 업로드할 수 있어요. 다운로드한 파일은 24시간 후 자동으로 삭제돼요.`}
        >
          <div className="flex flex-col gap-s-2">
            {PRESET_OPTIONS.map((o) => {
              const selected = preset === o.value
              return (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => setPreset(o.value)}
                  className={`flex w-full items-start justify-between gap-s-3 rounded-r-1 border px-s-4 py-s-3 text-left transition-colors duration-base ease-standard focus-visible:outline-none focus-visible:shadow-focus ${
                    selected
                      ? 'border-brand-pink bg-pink-50'
                      : 'border-line-hairline bg-surface-base hover:border-line-soft'
                  }`}
                >
                  <div>
                    <div className="flex items-center gap-s-2">
                      <span className="lf-h4 text-fg-1">{o.label}</span>
                      {o.recommended ? (
                        <span className="rounded-r-1 border border-brand-blue bg-brand-blue/10 px-s-2 py-px lf-eyebrow uppercase tracking-wide text-brand-blue">
                          권장 · Best
                        </span>
                      ) : null}
                    </div>
                    <p className="mt-s-1 lf-body-sm text-fg-2">
                      {o.description}
                    </p>
                  </div>
                </button>
              )
            })}
          </div>
        </Field>
      </div>

      <aside className="flex flex-col gap-s-4">
        <button
          type="submit"
          disabled={!file || busy}
          className="inline-flex w-full items-center justify-center gap-s-2 rounded-r-2 bg-brand-pink px-s-5 py-s-3 lf-h4 text-brand-brown transition-colors duration-base ease-standard hover:bg-pink-500 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Download size={16} strokeWidth={1.75} />
          {busy ? 'Uploading...' : 'Compress · 압축'}
        </button>
        <p className="lf-eyebrow flex items-start gap-s-2 text-fg-3">
          <span aria-hidden>▪</span>
          <span>
            Internal use only. 파일은 <span className="lf-num">24h</span> 후
            자동으로 만료돼요.
          </span>
        </p>
        {error ? (
          <p className="lf-body-sm text-status-danger">▪ {error}</p>
        ) : null}
      </aside>
    </form>
  )
}

/* -------------------------------------------------------------------------- */
/* Progress view                                                              */
/* -------------------------------------------------------------------------- */

function ProgressView({
  jobId,
  onComplete,
  onCancel,
}: {
  jobId: string
  onComplete: (job: PdfJob) => void
  onCancel: () => void
}) {
  const [job, setJob] = useState<PdfJob | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [canceling, setCanceling] = useState(false)

  useEffect(() => {
    let cancelled = false
    const tick = async () => {
      try {
        const j = await getPdfJob(jobId)
        if (cancelled) return
        setJob(j)
        if (j.status === 'completed') {
          onComplete(j)
          return
        }
        if (
          j.status === 'failed' ||
          j.status === 'expired' ||
          j.status === 'canceled'
        ) {
          setError(humanizeError(j.errorMessage || j.status))
          return
        }
        setTimeout(() => void tick(), POLL_MS)
      } catch (err) {
        if (cancelled) return
        setError(err instanceof Error ? humanizeError(err.message) : 'failed')
      }
    }
    void tick()
    return () => {
      cancelled = true
    }
  }, [jobId, onComplete])

  const onCancelClick = async () => {
    setCanceling(true)
    try {
      await cancelPdfJob(jobId)
    } catch {
      // ignore — onCancel below resets to landing regardless
    } finally {
      setCanceling(false)
      onCancel()
    }
  }

  return (
    <section className="mt-s-7 border border-line-hairline p-s-6">
      <p className="lf-eyebrow">▪ Compressing · 압축 중</p>
      <h2 className="mt-s-2 lf-h2 text-fg-1">
        {job?.originalFileName ?? 'Working...'}
      </h2>
      <p className="mt-s-1 lf-body-sm text-fg-3">
        <span className="lf-num">{job?.preset ?? '—'}</span> ·{' '}
        {job ? formatBytes(job.originalSizeBytes) : '—'}
      </p>

      <div className="mt-s-5 h-2 w-full bg-surface-soft">
        <div
          className="h-full bg-brand-pink transition-all duration-base ease-standard"
          style={{
            width: error
              ? '100%'
              : job?.status === 'compressing'
                ? '75%'
                : '20%',
          }}
        />
      </div>
      <p className="mt-s-2 lf-body-sm text-fg-3">
        Ghostscript는 보통 몇 초 안에 끝나요.
      </p>

      {error ? (
        <p className="mt-s-4 lf-body-sm text-status-danger">▪ {error}</p>
      ) : null}

      <div className="mt-s-5 flex gap-s-3">
        <button
          type="button"
          onClick={onCancelClick}
          disabled={canceling}
          className="inline-flex items-center gap-s-2 rounded-r-1 border border-line-hairline bg-surface-base px-s-3 py-s-2 lf-body-sm text-fg-2 transition-colors duration-base ease-standard hover:border-line-soft hover:text-fg-1 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <XIcon size={13} strokeWidth={1.5} />
          {canceling ? 'Canceling...' : 'Cancel · 취소'}
        </button>
      </div>
    </section>
  )
}

/* -------------------------------------------------------------------------- */
/* Complete view                                                              */
/* -------------------------------------------------------------------------- */

function CompleteView({
  job,
  onAgain,
}: {
  job: PdfJob
  onAgain: () => void
}) {
  const ratio = Math.max(0, Math.round(job.compressionRatio))
  return (
    <section className="mt-s-7 border border-line-hairline p-s-6">
      <p className="lf-eyebrow">▪ Done · 완료</p>
      <h2 className="mt-s-2 lf-h2 text-fg-1">{job.originalFileName}</h2>
      <p className="mt-s-1 lf-body-sm text-fg-3">
        <span className="lf-num">{job.preset}</span>
      </p>

      <div className="mt-s-5 grid grid-cols-3 gap-s-3 sm:max-w-[480px]">
        <Stat
          label="원본 · Original"
          value={formatBytes(job.originalSizeBytes)}
        />
        <Stat
          label="압축 후 · After"
          value={formatBytes(job.compressedSizeBytes)}
        />
        <Stat
          label="절감 · Saved"
          value={`${ratio}%`}
          tone={ratio > 0 ? 'positive' : 'neutral'}
        />
      </div>

      <div className="mt-s-6 flex flex-wrap gap-s-3">
        <a
          href={pdfDownloadUrl(job.jobId)}
          className="inline-flex items-center gap-s-2 rounded-r-2 bg-brand-pink px-s-5 py-s-3 lf-h4 text-brand-brown transition-colors duration-base ease-standard hover:bg-pink-500"
        >
          <Download size={16} strokeWidth={1.75} />
          Download · 다운로드
        </a>
        <button
          type="button"
          onClick={onAgain}
          className="inline-flex items-center gap-s-2 rounded-r-1 border border-line-hairline bg-surface-base px-s-3 py-s-2 lf-body-sm text-fg-2 transition-colors duration-base ease-standard hover:border-line-soft hover:text-fg-1"
        >
          Compress another · 다른 파일
        </button>
      </div>

      <p className="mt-s-4 lf-eyebrow text-fg-3">
        ▪ 파일은 24시간 후 자동으로 삭제돼요.
      </p>
    </section>
  )
}

/* -------------------------------------------------------------------------- */
/* History list                                                               */
/* -------------------------------------------------------------------------- */

function HistoryList({ items }: { items: PdfJob[] | null }) {
  if (items === null) return null
  if (items.length === 0) {
    return (
      <section className="mt-s-9">
        <p className="lf-eyebrow text-fg-3">▪ History · 최근 압축</p>
        <p className="mt-s-2 lf-body-sm text-fg-3">아직 없어요.</p>
      </section>
    )
  }
  return (
    <section className="mt-s-9">
      <p className="lf-eyebrow text-fg-3">▪ History · 최근 압축</p>
      <ul className="mt-s-3 border border-line-hairline">
        {items.map((j, i) => (
          <li
            key={j.jobId}
            className={`flex items-center justify-between gap-s-4 px-s-4 py-s-3 ${
              i > 0 ? 'border-t border-line-hairline' : ''
            }`}
          >
            <div className="min-w-0">
              <p className="line-clamp-1 lf-body text-fg-1">
                {j.originalFileName}
              </p>
              <p className="mt-s-1 lf-caption text-fg-3">
                <span className="lf-num">{j.preset}</span> ·{' '}
                <span className="lf-num">{formatBytes(j.originalSizeBytes)}</span>
                {j.status === 'completed' ? (
                  <>
                    {' → '}
                    <span className="lf-num">
                      {formatBytes(j.compressedSizeBytes)}
                    </span>
                    {' · '}
                    <span className="lf-num">
                      {Math.max(0, Math.round(j.compressionRatio))}% saved
                    </span>
                  </>
                ) : (
                  <>
                    {' · '}
                    <span className="lf-num">{j.status}</span>
                  </>
                )}
              </p>
            </div>
            {j.status === 'completed' ? (
              <a
                href={pdfDownloadUrl(j.jobId)}
                className="inline-flex flex-shrink-0 items-center gap-s-2 rounded-r-1 border border-line-hairline bg-surface-base px-s-3 py-s-2 lf-body-sm text-fg-1 transition-colors duration-base ease-standard hover:border-line-soft"
              >
                <Download size={13} strokeWidth={1.5} />
                Download
              </a>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  )
}

/* -------------------------------------------------------------------------- */
/* Atoms                                                                      */
/* -------------------------------------------------------------------------- */

function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <div>
      <p className="lf-eyebrow text-fg-3">▪ {label}</p>
      <div className="mt-s-3">{children}</div>
      {hint ? <p className="mt-s-2 lf-caption text-fg-3">{hint}</p> : null}
    </div>
  )
}

function Stat({
  label,
  value,
  tone = 'neutral',
}: {
  label: string
  value: string
  tone?: 'positive' | 'neutral'
}) {
  return (
    <div className="border border-line-hairline bg-surface-base p-s-4">
      <p className="lf-eyebrow text-fg-3">{label}</p>
      <p
        className={`mt-s-2 lf-h2 lf-num ${
          tone === 'positive' ? 'text-brand-blue' : 'text-fg-1'
        }`}
      >
        {value}
      </p>
    </div>
  )
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB']
  let i = 0
  let n = bytes
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024
    i++
  }
  const decimals = i >= 2 ? 1 : 0
  return `${n.toFixed(decimals)} ${units[i]}`
}

function humanizeError(code: string): string {
  switch (code) {
    case 'too_many_active':
      return '동시 압축 한도(2)에 도달했어요. 끝날 때까지 기다려 주세요.'
    case 'too_large':
      return '파일이 너무 커요. 조금 더 작은 파일로 다시 시도해 주세요.'
    case 'not_a_pdf':
      return 'PDF 형식이 아니에요.'
    case 'invalid_pdf':
      return 'PDF가 손상되었거나 Ghostscript가 처리할 수 없어요.'
    case 'invalid_preset':
      return '압축 강도가 올바르지 않아요.'
    case 'empty_or_invalid_body':
      return '파일이 비어있어요.'
    case 'gs_failed':
      return 'Ghostscript 처리 실패. 잠시 후 다시 시도해 주세요.'
    case 'stuck':
      return '작업이 멈춰서 자동 종료됐어요. 다시 시도해 주세요.'
    case 'canceled':
      return '취소되었어요.'
    case 'expired':
      return '파일이 만료되었어요 (24h 경과).'
    default:
      return `오류 (${code})`
  }
}
